import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";

const ICONS = ["HardHat", "Wrench", "Factory", "Building2", "Drill", "Settings", "Activity", "ShieldCheck", "Award"];
const empty = { code: "S/00", title: "", short: "", description: "", icon: "Wrench", image: "", highlights: [], order: 0 };

export default function ServicesManager({ onChange }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site");
      setList(data.services || []);
    } catch { toast.error("Could not load services"); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm({ ...empty, order: list.length + 1 }); };
  const startEdit = (s) => {
    setEditing(s.id);
    setForm({
      code: s.code || "",
      title: s.title || "",
      short: s.short || "",
      description: s.description || "",
      icon: s.icon || "Wrench",
      image: s.image || "",
      highlights: s.highlights || [],
      order: s.order || 0,
    });
  };
  const cancel = () => { setEditing(null); setForm(empty); };
  const ch = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error("Please upload or paste a service image."); return; }
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0, highlights: form.highlights.filter(Boolean) };
      if (editing === "new") await api.post("/admin/services", payload);
      else await api.patch(`/admin/services/${editing}`, payload);
      toast.success("Saved");
      cancel(); load(); onChange && onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete service "${s.title}"?`)) return;
    try { await api.delete(`/admin/services/${s.id}`); toast.success("Deleted"); load(); onChange && onChange(); }
    catch { toast.error("Delete failed"); }
  };

  if (editing !== null) {
    return (
      <div data-testid="service-editor" className="border border-[#1F3550] bg-[#0D1B2A] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-bold text-xl text-[#F8FAFC]">
            {editing === "new" ? "New Service" : "Edit Service"}
          </h3>
          <button onClick={cancel} data-testid="service-cancel" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#94A3B8] hover:text-[#F8FAFC]">
            <X className="h-4 w-4" /> Cancel
          </button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Code (e.g. S/01)" value={form.code} onChange={ch("code")} testid="svc-code" required />
          <Field label="Order" type="number" value={form.order} onChange={ch("order")} testid="svc-order" />
          <Field label="Title *" value={form.title} onChange={ch("title")} testid="svc-title" required />
          <div>
            <Label>Icon</Label>
            <select value={form.icon} onChange={ch("icon")} data-testid="svc-icon" className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none">
              {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><Label>Short tagline *</Label><Input data-testid="svc-short" value={form.short} onChange={ch("short")} required /></div>
          <div className="md:col-span-2"><Label>Description *</Label><TextArea data-testid="svc-desc" rows={4} value={form.description} onChange={ch("description")} required /></div>
          <div className="md:col-span-2">
            <ImageUpload label="Service image *" value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="svc-image" />
          </div>
          <div className="md:col-span-2">
            <Label>Highlights (one per line)</Label>
            <TextArea
              rows={5}
              data-testid="svc-highlights"
              value={(form.highlights || []).join("\n")}
              onChange={(e) => setForm({ ...form, highlights: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
            />
          </div>
          <div className="md:col-span-2">
            <button type="submit" disabled={saving} data-testid="svc-save" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] disabled:opacity-50">
              <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F39C12]">Services</div>
          <h2 className="mt-1 font-heading font-bold text-xl text-[#F8FAFC]">{list.length} services</h2>
        </div>
        <button onClick={startCreate} data-testid="svc-new" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910]">
          <Plus className="h-4 w-4" /> New Service
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s) => (
          <div key={s.id} data-testid={`svc-row-${s.id}`} className="border border-[#1F3550] bg-[#0D1B2A] overflow-hidden flex flex-col">
            <div className="aspect-[4/3] bg-[#050A0F]">
              {s.image && <img src={s.image} alt={s.title} className="w-full h-full object-cover" />}
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8]">{s.code}</div>
              <h3 className="mt-2 font-heading font-bold text-[#F8FAFC] leading-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-[#94A3B8] line-clamp-2">{s.short}</p>
              <div className="mt-auto pt-4 flex gap-2">
                <button onClick={() => startEdit(s)} data-testid={`svc-edit-${s.id}`} className="flex-1 inline-flex items-center justify-center gap-2 border border-[#1F3550] hover:border-[#F39C12] text-[#F8FAFC] py-2 text-[10px] uppercase tracking-widest font-bold transition-colors">
                  <Pencil className="h-3 w-3" /> Edit
                </button>
                <button onClick={() => remove(s)} data-testid={`svc-delete-${s.id}`} className="border border-[#1F3550] hover:border-[#EF4444] hover:text-[#EF4444] text-[#94A3B8] px-3 py-2 transition-colors">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full py-16 text-center text-[#94A3B8] border border-dashed border-[#1F3550]">No services yet.</div>}
      </div>
    </div>
  );
}

function Label({ children }) { return <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{children}</label>; }
function Input(props) { return <input {...props} className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" />; }
function TextArea(props) { return <textarea {...props} className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" />; }
function Field({ label, testid, ...props }) {
  return <div><Label>{label}</Label><input {...props} data-testid={testid} className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" /></div>;
}
